import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import { Save, Eye, EyeOff, Monitor, Smartphone, RotateCcw, Send, Search, RefreshCw, Languages } from "lucide-react";
import { api } from "@/lib/api";
import { CONTENT_SCHEMA, MEDIA_LIBRARY } from "@/lib/contentSchema";
import { AR } from "@/lib/i18n";
import ImageUpload from "@/components/admin/ImageUpload";

const PAGE_ROUTES = {
  Home: "/", About: "/about", Investment: "/investment",
  Partnerships: "/partnerships", "Saudi Arabia": "/saudi-arabia",
};

export default function ContentManager({ mode }) {
  const [textEn, setTextEn] = useState({});
  const [textAr, setTextAr] = useState({});
  const [images, setImages] = useState({});
  const [activePage, setActivePage] = useState(CONTENT_SCHEMA[0].page);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [hasDraft, setHasDraft] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [previewLang, setPreviewLang] = useState("en");
  const iframeRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/content", { params: { draft: true } });
      setTextEn(data.text_en || {});
      setTextAr(data.text_ar || {});
      setImages(data.images || {});
      setHasDraft(!!data.has_draft);
      setDirty(false);
    } catch { toast.error("Failed to load content."); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pushPreview = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: "intersect-preview", payload: { text_en: textEn, text_ar: textAr, images }, lang: previewLang },
      "*"
    );
  }, [textEn, textAr, images, previewLang]);

  useEffect(() => {
    if (!preview) return;
    const id = setTimeout(pushPreview, 250);
    return () => clearTimeout(id);
  }, [preview, pushPreview]);

  useEffect(() => {
    const onMsg = (e) => { if (e.data?.type === "intersect-preview-ready") pushPreview(); };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [pushPreview]);

  const save = async (status, nextImages) => {
    setSaving(status);
    try {
      await api.put("/admin/content", { text_en: textEn, text_ar: textAr, images: nextImages || images, status });
      setHasDraft(status === "draft");
      setDirty(false);
      toast.success(status === "draft" ? "Draft saved. Not visible on the live site yet." : "Published to the live site.");
    } catch { toast.error("Save failed."); } finally { setSaving(""); }
  };

  const discardDraft = async () => {
    if (!window.confirm("Discard the unpublished draft and restore the live content?")) return;
    try { await api.post("/admin/content/discard-draft"); toast.success("Draft discarded."); load(); }
    catch { toast.error("Could not discard draft."); }
  };

  const setEn = (src, v) => { setTextEn((p) => ({ ...p, [src]: v })); setDirty(true); };
  const setAr = (src, v) => { setTextAr((p) => ({ ...p, [src]: v })); setDirty(true); };

  const page = useMemo(() => CONTENT_SCHEMA.find((p) => p.page === activePage), [activePage]);
  const filteredGroups = useMemo(() => {
    if (!page) return [];
    const q = query.trim().toLowerCase();
    if (!q) return page.groups;
    return page.groups
      .map((g) => (g.title.toLowerCase().includes(q) ? g : { ...g, fields: g.fields.filter(([src, label]) => `${src} ${label}`.toLowerCase().includes(q)) }))
      .filter((g) => g.fields.length);
  }, [page, query]);

  const previewUrl = `${PAGE_ROUTES[activePage] || "/"}?preview=1&lang=${previewLang}`;

  if (loading) return <p className="text-muted-text">Loading content…</p>;

  if (mode === "media") {
    return (
      <div data-testid="admin-media">
        <div className="flex items-start justify-between mb-3 flex-wrap gap-4">
          <div>
            <p className="eyebrow mb-1">Assets</p>
            <h1 className="text-2xl md:text-3xl font-light text-ivory">Media Library</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => save("draft")} disabled={!!saving} className="btn-gold btn-sm" data-testid="media-save-draft">
              <Save size={14} strokeWidth={1.5} /> {saving === "draft" ? "Saving…" : "Save draft"}
            </button>
            <button onClick={() => save("published")} disabled={!!saving} className="btn-solid btn-sm" data-testid="media-save">
              <Send size={14} strokeWidth={1.5} /> {saving === "published" ? "Publishing…" : "Publish"}
            </button>
          </div>
        </div>
        <p className="text-sm text-muted-text mb-8">Replace the background and feature images used across the site. Leave blank to keep the built-in default.</p>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {MEDIA_LIBRARY.map(([key, label]) => (
            <div key={key} className="a-card p-5" data-testid={`media-${key}`}>
              <ImageUpload
                label={label}
                value={images[key] || ""}
                onChange={(v) => { setImages((prev) => ({ ...prev, [key]: v })); setDirty(true); }}
                testid={`media-${key}`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-pages">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-1">Content</p>
          <h1 className="text-2xl md:text-3xl font-light text-ivory">Page Content</h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {hasDraft
              ? <span className="a-chip text-amber-300" data-testid="content-status">Unpublished draft</span>
              : <span className="a-chip text-emerald-300" data-testid="content-status">Live</span>}
            {dirty && <span className="a-chip text-muted-text" data-testid="content-dirty">Unsaved changes</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setPreview((v) => !v)} className="btn-gold btn-sm" data-testid="content-toggle-preview">
            {preview ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />} {preview ? "Hide preview" : "Live preview"}
          </button>
          {hasDraft && (
            <button onClick={discardDraft} className="btn-gold btn-sm" data-testid="content-discard-draft">
              <RotateCcw size={14} strokeWidth={1.5} /> Discard draft
            </button>
          )}
          <button onClick={() => save("draft")} disabled={!!saving} className="btn-gold btn-sm" data-testid="content-save-draft">
            <Save size={14} strokeWidth={1.5} /> {saving === "draft" ? "Saving…" : "Save draft"}
          </button>
          <button onClick={() => save("published")} disabled={!!saving} className="btn-solid btn-sm" data-testid="content-save">
            <Send size={14} strokeWidth={1.5} /> {saving === "published" ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>
      <p className="text-sm text-muted-text mb-6">Edit every section in English and Arabic. Save a draft to keep working privately, then publish when ready. Leave a field blank to keep the built-in default.</p>

      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <div className="flex gap-2 flex-wrap flex-1">
          {CONTENT_SCHEMA.map((p) => (
            <button key={p.page} onClick={() => setActivePage(p.page)} data-testid={`page-tab-${p.page.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-4 py-2 text-xs uppercase tracking-wide rounded-full border transition-colors ${activePage === p.page ? "border-primary-gold text-primary-gold bg-primary-gold/10" : "border-white/10 text-muted-text hover:text-soft-stone"}`}>{p.page}</button>
          ))}
        </div>
        <div className="relative lg:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-text" />
          <input className="field pl-9 text-sm" placeholder="Search fields…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="content-search" />
        </div>
      </div>

      <div className={preview ? "grid grid-cols-1 xl:grid-cols-2 gap-6" : ""}>
        <div className={`space-y-10 ${preview ? "xl:max-h-[76vh] xl:overflow-y-auto xl:pr-3" : "max-w-4xl"}`}>
          {filteredGroups.length === 0 && <p className="text-muted-text text-sm">No fields match “{query}”.</p>}
          {filteredGroups.map((g) => (
            <div key={g.title}>
              <h3 className="text-xs tracking-wide uppercase text-primary-gold mb-5 border-b border-white/5 pb-2">{g.title}</h3>
              <div className="space-y-6">
                {g.fields.map(([src, label]) => (
                  <div key={src} className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="content-field">
                    <div>
                      <label className="eyebrow block mb-2">{label} <span className="text-muted-text lowercase tracking-normal">· English</span></label>
                      <textarea rows={2} className="field resize-none text-sm" placeholder={src}
                        value={textEn[src] || ""} onChange={(e) => setEn(src, e.target.value)} />
                    </div>
                    <div dir="rtl">
                      <label className="eyebrow block mb-2 text-right">{label} <span className="text-muted-text lowercase tracking-normal">· العربية</span></label>
                      <textarea rows={2} className="field ar-font resize-none text-sm text-right" placeholder={AR[src] || "النص العربي"}
                        value={textAr[src] || ""} onChange={(e) => setAr(src, e.target.value)} />
                      {AR[src] && !textAr[src] && <p className="text-[11px] text-muted-text mt-1 text-right">الترجمة المدمجة معروضة — اكتب نصاً لتجاوزها</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {preview && (
          <div className="a-card p-3 xl:sticky xl:top-4 h-fit" data-testid="content-preview">
            <div className="flex items-center justify-between mb-3 px-1 flex-wrap gap-2">
              <span className="text-xs uppercase tracking-wide text-primary-gold">Live preview · {activePage}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPreviewLang((l) => (l === "en" ? "ar" : "en"))} className="p-2 text-muted-text hover:text-primary-gold" title="Toggle language" data-testid="preview-lang">
                  <Languages size={15} strokeWidth={1.5} />
                </button>
                <button onClick={() => setDevice("desktop")} className={`p-2 ${device === "desktop" ? "text-primary-gold" : "text-muted-text hover:text-soft-stone"}`} title="Desktop" data-testid="preview-desktop"><Monitor size={15} strokeWidth={1.5} /></button>
                <button onClick={() => setDevice("mobile")} className={`p-2 ${device === "mobile" ? "text-primary-gold" : "text-muted-text hover:text-soft-stone"}`} title="Mobile" data-testid="preview-mobile"><Smartphone size={15} strokeWidth={1.5} /></button>
                <button onClick={pushPreview} className="p-2 text-muted-text hover:text-primary-gold" title="Refresh preview" data-testid="preview-refresh"><RefreshCw size={15} strokeWidth={1.5} /></button>
              </div>
            </div>
            <div className="bg-obsidian rounded-lg overflow-hidden flex justify-center">
              <iframe
                ref={iframeRef}
                key={`${activePage}-${previewLang}`}
                title="preview"
                src={previewUrl}
                onLoad={pushPreview}
                className="border-0 bg-obsidian"
                style={{ width: device === "mobile" ? 390 : "100%", height: "70vh" }}
              />
            </div>
            <p className="text-[11px] text-muted-text mt-2 px-1">Preview reflects your unsaved edits. Visitors see the published version until you press Publish.</p>
          </div>
        )}
      </div>
    </div>
  );
}
